import React, { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import {
  EditorView,
  getPMNodeCount,
  getPMNodeDOMMap,
  setNodePerformanceAttrs,
} from '@lyearn/lygo-core';
import { Blocks } from '@/components/PageEditor/types';
import { useSyncProgressMutation } from '@/modules/corporate/modules/course/graphql/mutation/generated/syncProgress';
import PerformanceBuffer from '@/modules/corporate/modules/course/utils/PerformanceBuffer';
import { CommandEnum, ResourceTypeEnum } from '@/types/schema';

import { nodeAttributes, performanceRegister } from '../../utils/performanceTrack';

// Interactive blocks (quiz/scorm/exercise/survey/roleplay) report completion via
// individualNodePerformanceCallback. syncProgress's response carries no updated lock/performance
// data, so lockSettings would otherwise only refresh whenever the syncPerformance subscription
// happens to catch up. Refetching here is a safety net that keeps the Next button's lock state in
// sync with the same user action that just changed it.
const REFETCH_ON_BLOCK_UPDATE_DEBOUNCE_MS = 300;

const useSyncPerformance = (
  contentType: ResourceTypeEnum,
  containerRef?: React.RefObject<HTMLDivElement>,
  courseId?: string,
  lessonId?: string,
  lessonPageId?: string,
  isEnabled?: boolean,
  journeyTraineeSessionId?: string | null,
  onBlockPerformanceUpdate?: () => void,
) => {
  const [editorView, setEditorView] = useState<EditorView>();
  const [, syncProgress] = useSyncProgressMutation();

  const perfBuffer = useMemo(
    () =>
      containerRef
        ? new PerformanceBuffer(function onFlush(events) {
            if (!events.length) {
              return;
            }

            syncProgress({ input: events })
              .then(() => {})
              .catch((e) => {
                ErrorReporter.error(e);
                console.error(`[syncProgress] ${lessonId} sync failed`);
              });
          })
        : undefined,
    [containerRef, lessonId, syncProgress],
  );

  const proseMirrorNodeDOMMap = useMemo(() => getPMNodeDOMMap(editorView), [editorView]);

  useEffect(() => {
    const scrollView = containerRef?.current;
    if (
      !isEnabled ||
      !scrollView ||
      !courseId ||
      !lessonId ||
      !lessonPageId ||
      // @ts-ignore
      !editorView?.docView
    ) {
      return;
    }

    console.debug(`[useSyncPerformance] ${lessonId} setting perf listeners`);

    const performanceCallback = (visibleNodesAttrs: nodeAttributes[]) => {
      console.debug(`[useSyncPerformance] ${lessonId} In performanceCallback`);
      for (const node of visibleNodesAttrs) {
        perfBuffer?.enqueue({
          target: {
            blockId: node.id,
            blockType: node.type.name,
            contentId: courseId,
            contentType,
            lessonId,
            lessonPageId,
          },
          attributes: {
            progress: 100,
          },
          journeyTraineeSessionId,
          clientTimestamp: new Date().toISOString(),
          command: CommandEnum.SetProgress,
        });
      }
    };

    if (getPMNodeCount(editorView) === 0) {
      perfBuffer?.enqueue({
        target: {
          blockId: lessonPageId,
          blockType: Blocks.LESSON_PAGE,
          lessonId,
          lessonPageId,
          contentId: courseId,
          contentType,
        },
        attributes: {
          progress: 100,
        },
        journeyTraineeSessionId,
        clientTimestamp: new Date().toISOString(),
        command: CommandEnum.SetProgress,
      });
    } else {
      // When editor gets mounted, manually call the performance register.
      // It is helpful when content size in the editor is not scrollable.
      performanceRegister(editorView, performanceCallback, proseMirrorNodeDOMMap);
    }

    const prosemirrorNodeAttrsDOMMap = new Map();
    const nodeIntersectionCallback = (entries: IntersectionObserverEntry[]) => {
      if (!prosemirrorNodeAttrsDOMMap.size) {
        return;
      }
      const visibleNodesAttrs: nodeAttributes[] = [];
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const nodeAttrs = prosemirrorNodeAttrsDOMMap.get(entry.target);
          if (nodeAttrs) {
            visibleNodesAttrs.push(nodeAttrs);
          }
        }
      });

      if (visibleNodesAttrs.length) {
        performanceCallback(visibleNodesAttrs);
      }
    };

    const containerObserver = new IntersectionObserver(nodeIntersectionCallback, {
      root: scrollView,
    });

    proseMirrorNodeDOMMap?.forEach((domNode, keyNode) => {
      containerObserver.observe(domNode);
      prosemirrorNodeAttrsDOMMap.set(domNode, { id: keyNode.attrs.id, type: keyNode.type });
    });

    return () => {
      perfBuffer?.flush();
      containerObserver.disconnect();

      console.debug(`[useSyncPerformance] ${lessonId} removing perf listeners`);
    };
  }, [
    containerRef,
    courseId,
    lessonId,
    perfBuffer,
    editorView,
    lessonPageId,
    proseMirrorNodeDOMMap,
    isEnabled,
    contentType,
    journeyTraineeSessionId,
  ]);

  const debouncedRefetchOnBlockUpdate = useMemo(
    () =>
      onBlockPerformanceUpdate
        ? debounce(onBlockPerformanceUpdate, REFETCH_ON_BLOCK_UPDATE_DEBOUNCE_MS)
        : undefined,
    [onBlockPerformanceUpdate],
  );

  useEffect(() => () => debouncedRefetchOnBlockUpdate?.cancel(), [debouncedRefetchOnBlockUpdate]);

  const individualNodePerformanceCallback = useCallback(
    (nodePerformanceAttrs: setNodePerformanceAttrs) => {
      if (!isEnabled) {
        return;
      }
      console.debug(`[useSyncPerformance] ${lessonId} In performanceCallback`);
      if (courseId && lessonId && lessonPageId) {
        perfBuffer?.enqueue({
          target: {
            blockId: nodePerformanceAttrs.id,
            blockType: nodePerformanceAttrs.blockType,
            contentId: courseId,
            contentType,
            lessonId,
            lessonPageId,
          },
          attributes: {
            progress: Math.min(nodePerformanceAttrs.progress || 0, 100),
            meta: nodePerformanceAttrs.meta,
          },
          journeyTraineeSessionId,
          clientTimestamp: new Date().toISOString(),
          command: CommandEnum.SetProgress,
        });
        // Interactive block (quiz/scorm/exercise/survey) reported an update — refresh lock
        // settings so the Next button's blocking-content state doesn't go stale.
        debouncedRefetchOnBlockUpdate?.();
      }
    },
    [
      isEnabled,
      lessonId,
      courseId,
      lessonPageId,
      perfBuffer,
      contentType,
      journeyTraineeSessionId,
      debouncedRefetchOnBlockUpdate,
    ],
  );

  // send a ping with timestamp to calculate time spent by a user on a particular lesson
  const addCurrentLessonTime = useCallback(() => {
    if (!isEnabled) {
      return;
    }
    console.debug(`[useSyncPerformance] ${lessonId} In addCurrentLessonTime`);
    if (courseId && lessonId && lessonPageId) {
      perfBuffer?.enqueue({
        target: {
          contentId: courseId,
          contentType,
          lessonId,
          lessonPageId,
        },
        journeyTraineeSessionId,
        clientTimestamp: new Date().toISOString(),
        command: CommandEnum.AddTime,
      });
    }
  }, [
    contentType,
    courseId,
    isEnabled,
    journeyTraineeSessionId,
    lessonId,
    lessonPageId,
    perfBuffer,
  ]);

  return {
    onCreateEditorView: useCallback((view: any) => {
      setEditorView(view);
    }, []),
    individualNodePerformanceCallback: individualNodePerformanceCallback,
    addCurrentLessonTime,
    editorView,
  };
};

export default useSyncPerformance;
